/* eslint-disable max-lines */
import {
	dateFormats,
	DateObject,
	type FanShopProductVariant,
	type FrontAuctionBidDTO,
	isWeb,
	type SnackbarVariants,
	useIntl,
	useSettings
} from '@capsicum/common';
import { type FanShopProductVariantAuction } from '@capsicum/features/fanshop/shared';
import { hasEnoughTokenCombinedWallet, useWalletFanToken } from '@capsicum/features/wallet/shared';
import { type NCWBalanceType, useMe, usePartners, useWalletsBalanceWrapper } from '@capsicum/socios-core';
import { useCallback, useMemo, useState } from 'react';

import { ProductTypes, useRPAuctionDetailsQueries } from '../data-access';
import { AuctionBidStatusEnum } from '../models';
import { getPriceForVariant } from '../util';

import { useAuctionProductUpdateFromWS } from './components/useAuctionProductUpdateFromWS';
import { useIsAuctionBidAllowed } from './useIsAuctionBidAllowed';

export type BidNotificationHandlerProps = {
	description: string;
	title: string;
	variant: SnackbarVariants;
};

type UseRPAuctionDetailsProps = {
	auctionId: string;
	bidNotificationHandler?: (props: BidNotificationHandlerProps) => void;
	getTokenBalanceBy: NCWBalanceType['getTokenBalanceBy'];
	isWebSocketDismissed?: boolean;
};

export const useRPAuctionDetails = ({
	auctionId,
	bidNotificationHandler,
	getTokenBalanceBy,
	isWebSocketDismissed = false
}: UseRPAuctionDetailsProps) => {
	const [refreshing, setRefreshing] = useState(false);
	const [, setCurrentStatus] = useState('');

	const { $t, getLocalizedField } = useIntl();

	const {
		settings: { isPoblanoEnabled, rewardsLinks }
	} = useSettings();

	const { me } = useMe();

	const {
		data: auctionData,
		isPending: auctionDataPending,
		refetch: refetchAuction
	} = useRPAuctionDetailsQueries(auctionId);

	const auctionPriceInterval = useMemo(
		() =>
			Number(
				auctionData?.redeemOptions?.priceInterval || auctionData?.product?.redeemOptions?.priceInterval || 0
			),
		[auctionData?.redeemOptions?.priceInterval, auctionData?.product?.redeemOptions?.priceInterval]
	);

	const hasStarted = useMemo(() => {
		return new DateObject().valueOf() - new DateObject(auctionData?.redeemableAt || '').valueOf() > 0;
	}, [auctionData]);

	const isActive = useMemo(() => {
		return (auctionData?.auctionStatus === 'opened' || auctionData?.auctionStatus === 'suspended') && hasStarted;
	}, [auctionData?.auctionStatus, hasStarted, auctionData]);

	const { isBidAllowed, isLoading: isLoadingBidAllowed } = useIsAuctionBidAllowed({
		auctionId: auctionId || '',
		isProductActive: isActive
	});

	const { getPartnerBy } = usePartners();
	const ssuPartner = useMemo(() => getPartnerBy(p => p.shortName === 'SSU')?.currencyId, [getPartnerBy]);

	const currentPartner = useMemo(
		() => getPartnerBy(p => p.id === auctionData?.product?.partnerId),
		[getPartnerBy, auctionData?.product?.partnerId]
	);

	const partnerCurrencyId = currentPartner?.currencyId;

	const { data: ssuWallet } = useWalletFanToken(ssuPartner);
	const { data: partnerWallet, isPending: isPendingCWallet } = useWalletFanToken(partnerCurrencyId);

	const handleWebSocketUpdate = useCallback(
		async (wsData: { payload: { status: AuctionBidStatusEnum } }) => {
			const bidStatus = wsData?.payload?.status;

			if (bidStatus !== AuctionBidStatusEnum.Accepted) return;

			const newAuctionData = await refetchAuction();
			const latestAcceptedBid = newAuctionData?.data?.auctionBids?.find(
				(bid: FrontAuctionBidDTO) => bid?.status === bidStatus
			);

			const outbidBids = newAuctionData?.data?.auctionBids?.filter(
				(bid: any) => bid.status === AuctionBidStatusEnum.Outbid
			);

			outbidBids?.sort(
				(a: any, b: any) => new DateObject(b.updatedAt).getTime() - new DateObject(a.updatedAt).getTime()
			);
			const previousAcceptedBid = outbidBids?.[0];

			const isOwnBid = me.externalUserId === latestAcceptedBid?.userReference?.chilizUserId;
			const userHasBeenOutbidded =
				latestAcceptedBid?.status === AuctionBidStatusEnum.Outbid &&
				me.externalUserId === previousAcceptedBid?.userReference?.chilizUserId;
			const notificationNameProp = isOwnBid ? AuctionBidStatusEnum.Accepted : AuctionBidStatusEnum.Outbid;

			if (isOwnBid || userHasBeenOutbidded) {
				bidNotificationHandler?.({
					description: $t(`rewards.rpAuctions.bid.${notificationNameProp}Message`),
					title: $t(`rewards.rpAuctions.bid.${notificationNameProp}Title`),
					variant: isOwnBid ? 'success' : 'pending'
				});
			}
		},
		[refetchAuction, auctionId, isWebSocketDismissed]
	);

	useAuctionProductUpdateFromWS(
		{ auctionId, productId: auctionData?.product?.id },
		handleWebSocketUpdate,
		isWebSocketDismissed
	);

	// Prevent edge case when a bid with index 0 has a status of 'outbidded'
	if (auctionData?.auctionBids) {
		auctionData.auctionBidsSorted = [...auctionData.auctionBids].sort((a, b) => {
			if (a.status === 'accepted' && b.status === 'outbid') return -1;

			return 0;
		});
	}

	const { hasEnoughToken } = partnerWallet ?? {};

	const isTicketType = auctionData?.product?.productType?.name === ProductTypes.Ticket;
	const isPhysicalTicket = auctionData?.product?.productType?.name === ProductTypes.Physical_Match_Tickets;
	const isExperienceType = auctionData?.product?.productType?.name === ProductTypes.Experience;
	const hasBids = useMemo(() => !!auctionData?.auctionBids?.length, [auctionData?.auctionBids]);

	const eventDate = dateFormats.format(auctionData?.eventDate, 'DD/MM/YYYY');
	const hasEventDate = eventDate && eventDate !== 'Invalid Date';
	const matchPlace = auctionData?.place;

	const getMinSsuAmount = useCallback(
		(v: FanShopProductVariantAuction = auctionData || {}) => {
			if (hasBids) {
				return Number(v?.auctionBids?.[0]?.amount || 0) + auctionPriceInterval;
			} else {
				return getPriceForVariant(v?.sellingPrice, v?.product?.sellingPrice);
			}
		},
		[auctionData, hasBids]
	);

	const { availableTokenBalance, totalBalance: totalRPBalance } = ssuWallet ?? {};

	const availableRPBalance = Number(availableTokenBalance);

	const hasLinkedProductOrder = auctionData?.hasLinkedProductOrder;
	const hasLinkedProduct =
		(auctionData?.product?.linkedByProducts?.length || 0) > 0 ||
		(auctionData?.product?.linkedProducts?.length || 0) > 0;

	const hasCheckoutCompleted = useMemo(() => auctionData?.hasCheckoutCompleted, [auctionData?.hasCheckoutCompleted]);

	const hasEnoughSsu = useMemo(
		() => availableRPBalance && availableRPBalance >= getMinSsuAmount(),
		[getMinSsuAmount, availableRPBalance]
	);

	const onRefresh = useCallback(async () => {
		setRefreshing(true);
		await refetchAuction();
		setRefreshing(false);
	}, [refetchAuction]);

	const auctionEndDate = auctionData?.endAt ? new DateObject(auctionData?.endAt) : null;
	const auctionStartDate = auctionData?.redeemableAt ? new DateObject(auctionData?.redeemableAt) : null;

	const getImages = useCallback((objWithImages?: FanShopProductVariant) => {
		return objWithImages?.images?.filter(image => {
			return isWeb() ? image?.imageType?.type?.includes('desktop') : !image?.imageType?.type?.includes('desktop');
		});
	}, []);

	const getCategory = useCallback(
		(v: FanShopProductVariant = auctionData || {}) => {
			const productCategories = v?.product?.categories;

			if (!productCategories) return null;

			return productCategories.map(c => getLocalizedField('name', c)).join(' / ');
		},
		[getLocalizedField, auctionData]
	);

	const hasFinished = useMemo(
		() => new DateObject().valueOf() - new DateObject(auctionData?.endAt || '').valueOf() > 0,
		[auctionData?.endAt]
	);

	const allImages = useMemo(() => {
		if (getImages(auctionData)?.length) {
			return getImages(auctionData) || [];
		}

		return getImages(auctionData?.product) || [];
	}, [getImages, auctionData]);

	const productDescription = useMemo(
		() => getLocalizedField('description', auctionData?.product || {}),
		[auctionData, getLocalizedField]
	);

	const getIsOwnBidByUserId = useCallback(
		(userId: string | undefined) => me.externalUserId === userId,
		[me.externalUserId]
	);

	const latestBid = auctionData?.auctionBids?.find((bid: FrontAuctionBidDTO) => bid?.status === 'accepted');

	const isOwnBid = getIsOwnBidByUserId(latestBid?.userReference?.chilizUserId);

	const currentBid = latestBid
		? Number(latestBid.amount)
		: getPriceForVariant(auctionData?.sellingPrice, auctionData?.product?.sellingPrice);

	const nextBid = latestBid
		? Number(latestBid.amount) + auctionPriceInterval
		: getPriceForVariant(auctionData?.sellingPrice, auctionData?.product?.sellingPrice);

	const isSkeletonLoading = auctionDataPending || isLoadingBidAllowed;

	const tokensRequired = useMemo(() => auctionData?.product?.productTierLevel?.minTokenRequired || 0, [auctionData]);

	const { getCurrencyBy } = useWalletsBalanceWrapper();

	const partnerCurrency = useMemo(
		() => getCurrencyBy(c => c.currency.id === partnerCurrencyId),
		[getCurrencyBy, partnerCurrencyId]
	);

	const NCWPartnerTokens = useMemo(
		() => getTokenBalanceBy(p => p.shortCode === partnerCurrency?.currency.shortCode),
		[getTokenBalanceBy, partnerCurrency?.currency.shortCode]
	);

	const balance = useMemo(
		() => Number(partnerWallet?.totalBalance || 0) + Number(NCWPartnerTokens?.amount || 0),
		[partnerWallet?.totalBalance, NCWPartnerTokens?.amount]
	);

	const hasEnoughFanTokens = useCallback(() => {
		if (isPoblanoEnabled) {
			return hasEnoughTokenCombinedWallet(tokensRequired, balance);
		} else {
			return hasEnoughToken?.(tokensRequired);
		}
	}, [tokensRequired, balance, hasEnoughTokenCombinedWallet, hasEnoughToken]);

	const amountNeeded = useMemo(
		() => (tokensRequired - balance > 0 ? tokensRequired - balance : 0),
		[tokensRequired, balance]
	);

	const partnerShortName = currentPartner?.shortName;

	const isBidEnabled = useMemo(() => {
		return hasEnoughFanTokens() && hasStarted && auctionData?.redeemableAt;
	}, [hasEnoughFanTokens, hasStarted, auctionData?.redeemableAt]);

	const texts = useMemo(
		() => ({
			accompaniedMinors: $t('rewards.rpAuctions.accompaniedMinors'),
			buyFanTokens: $t('rewards.buyFanTokens'),
			closed: $t('rewards.rpAuctions.closed'),
			comingSoon: $t('rewards.fanshop.comingSoon'),
			details: $t('rewards.rpAuctions.productDetails'),
			eventDate: $t('rewards.rpAuctions.eventDate'),
			faqTitle: $t('rewards.rpAuctions.faqTitle'),
			importantInformation: $t('rewards.rpAuctions.important'),
			insufficientRPDescription: $t('rewards.rpAuctions.insufficientRPDescription'),
			insufficientRPTitle: $t('rewards.rpAuctions.insufficentRPTitle'),
			matchDate: $t('rewards.rpAuctions.matchDate'),
			placeABid: $t('rewards.rpAuctions.placeABid'),
			processing: $t('rewards.rpAuctions.processing'),
			prohibitedResale: $t('rewards.rpAuctions.prohibitedResale'),
			redeem: $t('rewards.rpAuctions.redeem'),
			stadium: $t('rewards.rpAuctions.stadium'),
			tokensNeeded: $t('rewards.fanshop.product.tokenNeeded', {
				amount: amountNeeded,
				token: partnerShortName
			})
		}),
		[$t, amountNeeded]
	);

	const faqLink = rewardsLinks.auctionsFaq;

	return {
		allImages,
		auctionData,
		auctionEndDate,
		auctionStartDate,
		availableRPBalance,
		currentBid,
		currentPartner,
		eventDate,
		faqLink,
		getCategory,
		getIsOwnBidByUserId,
		getMinSsuAmount,
		hasBids,
		hasCheckoutCompleted,
		hasEnoughFanTokens,
		hasEnoughSsu,
		hasEventDate,
		hasFinished,
		hasLinkedProduct,
		hasLinkedProductOrder,
		hasStarted,
		isActive,
		isBidAllowed,
		isBidEnabled,
		isExperienceType,
		isOwnBid,
		isPendingCWallet,
		isPhysicalTicket,
		isSkeletonLoading,
		isTicketType,
		matchPlace,
		nextBid,
		onRefresh,
		productDescription,
		refetchAuction,
		refreshing,
		setCurrentStatus,
		texts,
		totalRPBalance
	};
};
